// The diagrams on Start here's screens (live's Visuals: config.visual, 1 or a list), in POYT's look:
// - waterfall: the cash offer built backwards from the end value (on a phone it lies on its side, so
//   its words stay readable at 390);
// - comps: what finished comparable evidence looks like (a table; on a phone each comparable is a card);
// - rent: which asking rents are evidence;
// - offers: the 2 offers, 1 built and 1 deliberately not;
// - funnel: a town narrowed to named streets;
// - node: near a station or hospital, not on it;
// - criteria: keep, amber and avoid, before they judge streets themselves.
// Live draws green for a let rent, a keeper and the sweet spot; POYT's look never uses green, so
// those are navy here, and what to avoid is orange. Colours come from tokens.css through
// poyt-start.css.
import { useMedia } from '../../../lib/media.js'
import { COPY, gbp } from './copy.js'
import { Md } from './Md.jsx'

export function Visuals({ spec }) {
  if (!spec) return null
  const list = Array.isArray(spec) ? spec : [spec]
  return list.map((v, i) => <Visual key={i} v={v || {}} />)
}

function Visual({ v }) {
  const inner = v.kind === 'waterfall' ? <Waterfall d={v} />
    : v.kind === 'comps' ? <Comps d={v} />
      : v.kind === 'rent' ? <Rent d={v} />
        : v.kind === 'offers' ? <Offers d={v} />
          : v.kind === 'funnel' ? <Funnel d={v} />
            : v.kind === 'node' ? <NodeRings d={v} />
              : v.kind === 'criteria' ? <Criteria d={v} />
                : null
  if (!inner) return null
  // live draws the offers and the criteria without the frame around them
  const bare = v.kind === 'offers' || v.kind === 'criteria'
  return (
    <figure className={bare ? 'ps-vis ps-vis--bare' : 'ps-vis'} data-kind={v.kind}>
      {v.title && <figcaption className="ps-vis-head">{v.title}</figcaption>}
      {v.sub && <p className="ps-vis-sub">{v.sub}</p>}
      {inner}
      {v.foot && <Md text={v.foot} className="ps-vis-foot" />}
    </figure>
  )
}

// A navy strip of figures under a diagram (live's tbl-note)
function Figures({ items }) {
  const shown = items.filter(x => x && x.value)
  if (!shown.length) return null
  return (
    <dl className="ps-figs">
      {shown.map(x => (
        <div key={x.label} className="ps-figs-item"><dt>{x.label}</dt><dd>{x.value}</dd></div>
      ))}
    </dl>
  )
}

// "£200k", "-£50k"
const k = n => `${n < 0 ? '-' : ''}£${Math.round(Math.abs(n) / 1000)}k`

function Waterfall({ d }) {
  // narrower than 900, the bars lie across, so the words stay readable (live's drawing shrinks them)
  const narrow = useMedia('(max-width: 900px)')
  const phone = useMedia('(max-width: 760px)')
  const value = Number(d.value) || 200000
  const pctN = Number(d.pct) || 75
  const pct = pctN / 100
  const refurb = Number(d.refurb) || 0
  const costs = Number(d.costs) || 0
  const p75 = value * pct
  const afterRefurb = p75 - refurb
  const offer = afterRefurb - costs
  const gap = value - offer
  const bars = [
    { i: 0, lo: 0, hi: value, cls: 'ps-wf-value', name: d.valueLabel || COPY.finishedValue, amt: value },
    { i: 1, lo: p75, hi: value, cls: 'ps-wf-gap', name: COPY.gapBar, amt: -(value - p75) },
    { i: 2, lo: afterRefurb, hi: p75, cls: 'ps-wf-cut', name: COPY.refurbBar, amt: -refurb },
    { i: 3, lo: offer, hi: afterRefurb, cls: 'ps-wf-cut', name: COPY.costsBar, amt: -costs },
    { i: 4, lo: 0, hi: offer, cls: 'ps-wf-offer', name: COPY.offerBar, amt: offer },
  ]
  return (
    <div>
      {narrow ? <WaterfallAcross bars={bars} value={value} p75={p75} pct={pctN} width={phone ? 340 : 560} /> : <WaterfallUp bars={bars} value={value} p75={p75} offer={offer} pct={pctN} />}
      <Figures items={[
        { label: COPY.theyThink, value: gbp(value) },
        { label: COPY.youSay, value: gbp(offer) },
        { label: COPY.gapOutLoud, value: gbp(gap) },
      ]} />
    </div>
  )
}

// Live's own drawing: the bars standing up, 720 by 300
function WaterfallUp({ bars, value, p75, offer, pct }) {
  const L = 66; const R = 694; const T = 26; const B = 250; const n = 5; const colW = (R - L) / n; const barW = 76
  const y = v => B - (v / value) * (B - T)
  const cx = i => L + colW * i + colW / 2
  return (
    <svg className="ps-svg" viewBox="0 0 720 300" role="img" aria-label={COPY.waterfallLabel}>
      <line className="ps-wf-line" x1={L - 6} y1={y(p75)} x2={R + 4} y2={y(p75)} />
      <text className="ps-wf-tag" x={R + 4} y={y(p75) - 7} textAnchor="end">{COPY.lendersLine(pct)}</text>
      {bars.map(b => {
        const top = y(b.hi)
        const h = Math.max(2, y(b.lo) - y(b.hi))
        return (
          <g key={b.i}>
            {b.i > 0 && b.i < 4 && <line className="ps-wf-link" x1={cx(b.i - 1) + barW / 2} y1={top} x2={cx(b.i) - barW / 2} y2={top} />}
            {b.i === 4 && <line className="ps-wf-link" x1={cx(3) - barW / 2} y1={y(offer)} x2={cx(4) - barW / 2} y2={y(offer)} />}
            <rect className={b.cls} x={cx(b.i) - barW / 2} y={top} width={barW} height={h} rx="3" />
            <text className="ps-wf-name" x={cx(b.i)} y={B + 20} textAnchor="middle">{b.name}</text>
            <text className={b.i === 4 ? 'ps-wf-amt ps-wf-amt--offer' : 'ps-wf-amt'} x={cx(b.i)} y={B + 40} textAnchor="middle">{k(b.amt)}</text>
          </g>
        )
      })}
      <line className="ps-wf-axis" x1={L - 6} y1={B} x2={R + 4} y2={B} />
    </svg>
  )
}

// On a phone: the same bars lying across, 1 a row, so the words stay at their size
function WaterfallAcross({ bars, value, p75, pct, width = 340 }) {
  const W = width; const rowH = 56; const top = 6; const height = top + bars.length * rowH + 24
  const x = v => (v / value) * W
  return (
    <svg className="ps-svg ps-svg--across" viewBox={`0 0 ${W} ${height}`} role="img" aria-label={COPY.waterfallLabel}>
      <line className="ps-wf-line" x1={x(p75)} y1={top} x2={x(p75)} y2={top + bars.length * rowH} />
      {bars.map((b, n) => {
        const y0 = top + n * rowH
        return (
          <g key={b.i}>
            <text className="ps-wf-name ps-wf-name--across" x={0} y={y0 + 14}>{b.name}</text>
            <text className={b.i === 4 ? 'ps-wf-amt ps-wf-amt--offer' : 'ps-wf-amt'} x={W} y={y0 + 15} textAnchor="end">{k(b.amt)}</text>
            <rect className={b.cls} x={x(b.lo)} y={y0 + 24} width={Math.max(2, x(b.hi) - x(b.lo))} height={18} rx="3" />
          </g>
        )
      })}
      <text className="ps-wf-tag" x={Math.min(W, x(p75) + 2)} y={height - 6} textAnchor="end">{COPY.lendersLine(pct)}</text>
    </svg>
  )
}

const TAGS = { ceil: () => COPY.ceiling, floor: () => COPY.floor, out: () => COPY.excluded }

function Comps({ d }) {
  const rows = Array.isArray(d.rows) ? d.rows : []
  const cols = [COPY.colComparable, COPY.colType, COPY.colSold, COPY.colPrice, COPY.colCondition]
  return (
    <div>
      <table className="ps-tbl ps-tbl--stack">
        <thead><tr>{cols.map(c => <th key={c} scope="col">{c}</th>)}<td aria-hidden="true" /></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={r.tag === 'out' ? 'ps-tbl-out' : ''}>
              <td className="ps-tbl-lead" data-label={cols[0]}>{r.addr}</td>
              <td data-label={cols[1]}>{r.type}</td>
              <td data-label={cols[2]}>{r.sold}</td>
              <td className="ps-tbl-num" data-label={cols[3]}>{r.price}</td>
              <td data-label={cols[4]}>{r.condition}</td>
              <td className="ps-tbl-tagcell">{TAGS[r.tag] && <span className={`ps-tag ps-tag--${r.tag}`}>{TAGS[r.tag]()}</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {(d.asis || d.gdv) && (
        <Figures items={[
          { label: COPY.asIsValue, value: d.asis },
          { label: COPY.gdv, value: d.gdv },
          { label: COPY.comparablesUsed, value: d.used },
        ]} />
      )}
    </div>
  )
}

function Rent({ d }) {
  const rows = Array.isArray(d.rows) ? d.rows : []
  const max = Math.max(1, ...rows.map(r => Number(r.days) || 1))
  return (
    <div>
      <ul className="ps-rent">
        {rows.map((r, i) => (
          <li key={i} className={r.let ? 'ps-rent-row' : 'ps-rent-row ps-rent-row--asking'}>
            <span className="ps-rent-amt">
              <b>{r.rent}</b>
              <span>{r.let ? COPY.let : COPY.stillAsking}</span>
            </span>
            <span className="ps-rent-bar-wrap">
              <span className="ps-rent-track"><span className="ps-rent-bar" style={{ width: `${Math.round(((Number(r.days) || 0) / max) * 100)}%` }} /></span>
              <span className="ps-rent-note">{COPY.daysOnMarket(r.days, r.note)}</span>
            </span>
          </li>
        ))}
      </ul>
      {d.use && <Figures items={[{ label: COPY.inYourStack, value: d.use }]} />}
    </div>
  )
}

function Offers({ d }) {
  const a = d.cash || {}
  const b = d.creative || {}
  return (
    <div className="ps-split">
      <div className="ps-offer ps-offer--built">
        <div className="ps-offer-n">{a.n || COPY.offerOne}</div>
        <h3 className="ps-offer-h">{a.h || COPY.cashOffer}</h3>
        <ul>{(a.items || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
        <div className="ps-offer-stamp">{a.stamp || COPY.buildToday}</div>
      </div>
      <div className="ps-offer ps-offer--locked">
        <div className="ps-offer-n">{b.n || COPY.offerTwo}</div>
        <h3 className="ps-offer-h">{b.h || COPY.creativeOffer}</h3>
        <ul className="ps-offer-blur" aria-hidden="true">{(b.items || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
        <div className="ps-offer-stamp">{b.stamp || COPY.buildWeeks}</div>
      </div>
    </div>
  )
}

function Funnel({ d }) {
  const st = Array.isArray(d.stages) ? d.stages : []
  const n = st.length
  return (
    <ol className="ps-funnel" aria-label={COPY.funnelLabel}>
      {st.map((s, i) => {
        const w = 100 - (100 - 43) * (i / Math.max(1, n - 1))
        const tone = i === n - 1 ? 'last' : i === n - 2 ? 'deep' : 'mid'
        return (
          <li key={i} className={`ps-funnel-step ps-funnel-step--${tone}`} style={{ width: `${w}%` }}>
            <b>{s.label}</b>
            <span>{s.note}</span>
          </li>
        )
      })}
    </ol>
  )
}

function NodeRings({ d }) {
  const rings = Array.isArray(d.rings) ? d.rings : []
  const cx = 200; const cy = 155; const R = [128, 92, 52]
  const tones = ['on', 'sweet', 'far']
  return (
    <div className="ps-node">
      <svg className="ps-svg ps-node-svg" viewBox="0 0 400 310" role="img" aria-label={COPY.nodeLabel}>
        <circle className="ps-node-far" cx={cx} cy={cy} r={R[0]} />
        <circle className="ps-node-sweet" cx={cx} cy={cy} r={R[1]} />
        <circle className="ps-node-on" cx={cx} cy={cy} r={R[2]} />
        <circle className="ps-node-centre" cx={cx} cy={cy} r="17" />
        <text className="ps-node-centre-text" x={cx} y={cy + 4} textAnchor="middle">{d.centre || COPY.node}</text>
        <text className="ps-node-sweet-text" x={cx} y={cy + R[1] - 14} textAnchor="middle">{d.sweetLabel || COPY.sweetSpot}</text>
      </svg>
      <ul className="ps-node-key">
        {rings.map((r, i) => (
          <li key={i} className={`ps-node-key-item ps-node-key-item--${tones[i] || 'far'}`}>
            <span className="ps-node-dot" aria-hidden="true" />
            <span className="ps-node-key-text"><b>{r.h}</b><span>{r.b}</span>{r.b2 && <span>{r.b2}</span>}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Criteria({ d }) {
  const cols = Array.isArray(d.cols) ? d.cols : []
  return (
    <div className="ps-crit">
      {cols.map((c, i) => (
        <div key={i} className={`ps-crit-col ps-crit-col--${c.kind}`}>
          <div className="ps-crit-h"><span className="ps-crit-dot" aria-hidden="true" />{c.h}</div>
          <ul>{(c.items || []).map((x, j) => <li key={j}>{x}</li>)}</ul>
        </div>
      ))}
    </div>
  )
}
